import fetcher, { FleetPaginated, FleetQueryParams } from '../fetcher';
import { IndustryMasterData, MasterData } from './master-data';
import { Referrer } from './prospect';
import {
	CustomerBusinessDocument,
	CustomerLegalDocument,
	ExistingFinancingLimitInformation,
	FinancingApplicationLimitInformation,
} from './types';

export type Pipeline<T extends 'payload' | '' = ''> = T extends 'payload'
	? {
			id: number;
			prospectId: string;
			pipelinePlafonTypeIds: number[];
			ultimateOwner: string;
			currentIssue: string;
			creditScheme: string;
			approvalNotes: string;
			creditReview: string;
			creditRecommendation: string;
			recommendationNotes: string;
			forumPipelineCheck: boolean;
			creditNotes: string;
			plafonType: number;
		}
	: {
			pipelineHotProspectId: number;
			pipelineNo: string;
			no: number;
			prospectId: string;
			orderSource: {
				id: number;
				value: string;
			};
			customerId: string;
			customerName: string;
			prospectStatus: {
				id: string;
				value: string;
			};
			pipelineStatus: {
				id: string;
				value: string;
			};
			customerGroup: {
				id: number;
				value: string;
			};
			industrySector: {
				id: number;
				sectorLevel1: string;
				sectorLevel2: string;
				sectorLevel3: string;
				sectorLevel4: string;
				sectorLevel5: string;
				industryClass: string;
				ecosystem: string;
				portofolioGuidelineId: string | number;
				portofolioGuideline: string;
			};
			newOrderDate: string;
			ultimateOwner: string;
			currentIssue: string;
			creditScheme: string;
			approvalNotes: string;
			creditReview: string;
			frmName: string;
			creditRecommendation: {
				id: string | number;
				value: string;
			};
			creditNotes: string;
			outstandingPrincipal: number;
			existingFinancingLimit: number;
			limitApplication: number;
			referrerUnitName: string;
			businessHeadName: string;
			businessDivisionHeadName: string;
			plafonType: {
				id: number;
				value: string;
			};
			pipelinePlafonTypeIds: number[];
			prospectInformation: {
				existingFinancingLimitInformation: ExistingFinancingLimitInformation[];
				financingApplicationLimitInformation: FinancingApplicationLimitInformation[];
			};
			napNo?: string;
		};

export type ApprovalHistory = {
	no: number;
	approvalProcess: string;
	approvalDate: string;
	approvalBy: string;
	approvalNote: string;
	approvalStatus: string;
};

export type PipelineDetail = {
	customerDocuments: {
		customerLegalDocuments: CustomerLegalDocument[];
		customerBusinessDocuments: CustomerBusinessDocument[];
		customerReferralDocuments: CustomerLegalDocument[];
	};
	pipelineApprovalHistory: {
		no: number;
		approvalBy: string;
		approvalDate: string;
		approvalNote: string;
		approvalStatus: string;
		approvalProcess: string;
	}[];
	pipelineDetail: Pipeline;
	prospectInformation: {
		existingFinancingLimitInformation: ExistingFinancingLimitInformation[];
		financingApplicationLimitInformation: FinancingApplicationLimitInformation[];
	};
};

export type ApprovalListItem = {
	id: number;
	approvalStatus: number;
	notes: string;
};

export type PipelineItems = {
	pipelineHotProspectId?: number;
	pipelineNo?: string;
	napNo?: string;
	prospectId: string;
	customerName: string;
	customerId: string;
	customerGroup: MasterData;
	existingFinancingLimit: number;
	industrySector: IndustryMasterData;
	outstandingPrincipal: number;
	limitApplication: number;
	plafonType: MasterData;
	referrer: Referrer;
	orderSource: MasterData;
	segmentMarket: string;
	referrerUnitName: string;
	prospectStatus: { id: string; value: string };
	ultimateOwner: string;
	currentIssue: string;
	creditScheme: string;
	newOrderDate: string;
	pipelineStatus: { id: string; value: string };
	financingPurpose: string;
	frmName: string;
	businessHeadName: string;
};

export enum ProspectSteps {
	Bch = 1, // Business Unit Checking
	Bap, // Business Unit Approval
	Bapv, // Business Unit Approved
	Cas, // Credit Unit Assign
	Cch, // Credit Unit Checking
	Cap, // Credit Unit Approval
	Capv, // Credit Unit Approved - Customer Checking
	SbCom, // Survey Business Completed
	SbReq, // Survey Business Requested
	SbApp, // Survey Business Approved - Data Pra Survey
	SbRet, // Survey Business Returned
	ScReq, // Survey Credit Requested
	ScApp, // Survey Credit Approved - Analisa Pra
	ScRej, // Survey Credit Rejected
	ScRet, // Survey Credit Returned
	SfhAp, // Survey Fleet Head Approved
	SfhRj, // Survey Fleet Head Rejected
	SchAp, // Survey Credit Head Approved
	SchRj, // Survey Credit Head Rejected
	VbRrj, // Visit Report Business Request Reject
	VbReq, // Visit Report Business Request Approval
	VbApp, // Visit Report Business Request Approved
	VbRet, // Visit Report Business Request Returned
	VbRej, // Visit Report Business Request Rejected
	VcRve, // Visit Report Credit Request Verification
	VcApp, // Visit Report Credit Request Approved
	VcRet, // Visit Report Credit Request Returned
	VcRej, // Visit Report Credit Request Rejected
	VfhAp, // Visit Report Fleet Head Request Approved
	VfhRt, // Visit Report Fleet Head Request Returned
	VchAp, // Visit Report Credit Head Request Approved
	VchRt, // Visit Report Credit Head Request Returned
}

export const getPipelineList = (params?: FleetQueryParams) => {
	const defaultParams: FleetQueryParams = params ?? {
		pagination: { pageSize: 20, pageNumber: 1 },
	};
	return fetcher<FleetPaginated<Pipeline>>({
		api: '/pipelines/hot-prospect/get-list',
		method: 'POST',
		body: defaultParams,
	});
};

export const getPipelineDetail = (pipelineId: number) => {
	return fetcher<PipelineDetail>({
		api: '/pipelines/hot-prospect/get-detail',
		method: 'POST',
		body: { id: pipelineId },
	});
};

export const getPipelineDetailFRM = (prospectId: string) => {
	return fetcher<PipelineDetail>({
		api: '/pipelines/hot-prospect/frm/get-detail',
		method: 'POST',
		body: { prospectId },
	});
};

export const updatePipelineFRM = (body: Pipeline<'payload'>) => {
	return fetcher({
		api: '/pipelines/hot-prospect/frm/update',
		body: {
			pipelineHotProspectDto: body,
			action: 'Update',
		},
	});
};

export const savePipelineFRM = (body: Pipeline<'payload'>) => {
	return fetcher<{ message: string; pipelineNo: string }>({
		api: '/pipelines/hot-prospect/frm/submit-request',
		body: {
			pipelineHotProspectDto: body,
			action: 'Submit',
		},
	});
};

export const updatePipelineFBDH = (body: Pipeline<'payload'>) => {
	return fetcher({
		api: '/pipelines/hot-prospect/fbdh/update',
		body: {
			pipelineHotProspectDto: body,
			action: 'Update',
		},
	});
};

export const savePipelineFBDH = (body: Pipeline<'payload'>) => {
	return fetcher({
		api: '/pipelines/hot-prospect/fbdh/approve-hot-prospect',
		body: {
			pipelineHotProspectDto: body,
			action: 'Submit',
		},
	});
};

export const savePipelineCCA = (body: Pipeline<'payload'>) => {
	return fetcher({
		api: '/pipelines/hot-prospect/cca/approve-hot-prospect',
		body: {
			pipelineHotProspectDto: body,
			action: 'Submit',
		},
	});
};

export const updatePipelineCCDH = (body: Pipeline<'payload'>) => {
	return fetcher({
		api: '/pipelines/hot-prospect/ccdh/update',
		body: {
			pipelineHotProspectDto: body,
			action: 'Submit',
		},
	});
};

export const approvePipelineCCDH = (body: {
	pipelineHotProspectIds: number[];
	approvalNotes: string;
}) => {
	return fetcher({
		api: '/pipelines/hot-prospect/ccdh/approve-hot-prospect',
		body,
	});
};

export const submitPipelineCCDH = (body: ApprovalListItem[]) => {
	return fetcher({
		api: '/pipelines/hot-prospect/ccdh/submit-summary-pipeline',
		body: {
			pipelineApprovals: body,
		},
	});
};

export const rejectPipelineCCDH = (body: {
	pipelineHotProspectIds: number[];
	approvalNotes: string;
}) => {
	return fetcher({
		api: '/pipelines/hot-prospect/ccdh/return-hot-prospect',
		body,
	});
};

export const approvePipelineFH = (body: {
	pipelineHotProspectIds: number[];
	approvalNotes: string;
}) => {
	return fetcher({
		api: '/pipelines/hot-prospect/fh/approve-hot-prospect',
		body,
	});
};

export const rejectPipelineFH = (body: {
	pipelineHotProspectIds: number[];
	approvalNotes: string;
}) => {
	return fetcher({
		api: '/pipelines/hot-prospect/fh/reject-hot-prospect',
		body,
	});
};

export const approvePipelineCH = (body: {
	pipelineHotProspectIds: number[];
	approvalNotes: string;
}) => {
	return fetcher({
		api: '/pipelines/hot-prospect/ch/approve-hot-prospect',
		body,
	});
};

export const rejectPipelineCH = (body: {
	pipelineHotProspectIds: number[];
	approvalNotes: string;
}) => {
	return fetcher({
		api: '/pipelines/hot-prospect/ch/reject-hot-prospect',
		body,
	});
};
